/**
 * Thin fetch wrapper. Every call attaches a fresh API access token and
 * surfaces a useful error from the API's JSON error envelope.
 *
 * All verbs accept an optional `RequestInit` so callers can pass an
 * `AbortSignal` (used by the pending-ops toast to let the user cancel
 * long-running fetches).
 */

export type ApiError = Error & { status: number; payload: unknown };

export class ApiClient {
  constructor(private readonly getToken: () => Promise<string>) {}

  async request<T>(input: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken();
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${token}`);
    if (init.body && !headers.has("content-type")) {
      headers.set("content-type", "application/json");
    }

    const res = await fetch(input, { ...init, headers });
    if (!res.ok) {
      let payload: unknown = null;
      try { payload = await res.json(); } catch { /* non-JSON */ }
      const err = new Error(`API ${res.status}: ${res.statusText}`) as ApiError;
      err.status = res.status;
      err.payload = payload;
      throw err;
    }
    if (res.status === 204) return undefined as unknown as T;
    return (await res.json()) as T;
  }

  get<T>(path: string, init?: RequestInit) {
    return this.request<T>(path, init);
  }
  post<T>(path: string, body: unknown, init?: RequestInit) {
    return this.request<T>(path, { ...init, method: "POST", body: JSON.stringify(body) });
  }
  patch<T>(path: string, body: unknown, init?: RequestInit) {
    return this.request<T>(path, { ...init, method: "PATCH", body: JSON.stringify(body) });
  }
  del<T>(path: string, init?: RequestInit) {
    return this.request<T>(path, { ...init, method: "DELETE" });
  }
}
